import React from 'react';

/**
 * Catches render-time errors anywhere in its subtree and shows a recoverable
 * message instead of a blank white screen. "Réessayer" re-renders in place
 * (no full page refresh); "Recharger" does a hard reload as a last resort.
 *
 * `resetKey` — when this prop changes (e.g. the page or agency changes), the
 * boundary auto-clears its error so navigation recovers without manual refresh.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary] Render error:', error, info?.componentStack);
  }

  componentDidUpdate(prevProps) {
    // Auto-recover when the surrounding context changes (e.g. user navigated).
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const isAr = this.props.lang === 'ar';
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6" dir={isAr ? 'rtl' : 'ltr'}>
        <div className="max-w-md w-full bg-white border border-gray-200 rounded-2xl shadow-sm p-8 text-center">
          <div className="text-4xl mb-3">⚠️</div>
          <h2 className="text-lg font-black text-gray-900 mb-1">
            {isAr ? 'حدث خطأ غير متوقع' : 'Une erreur inattendue est survenue'}
          </h2>
          <p className="text-sm text-gray-500 mb-5">
            {isAr
              ? 'يمكنك إعادة المحاولة دون فقدان جلستك.'
              : 'Vous pouvez réessayer sans perdre votre session.'}
          </p>
          <div className="flex gap-2 justify-center">
            <button
              onClick={() => this.setState({ hasError: false, error: null })}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-sm"
            >
              {isAr ? 'إعادة المحاولة' : 'Réessayer'}
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold text-sm border border-gray-200"
            >
              {isAr ? 'إعادة تحميل' : 'Recharger'}
            </button>
          </div>
          {this.state.error?.message && (
            <p className="mt-4 text-[11px] text-gray-400 font-mono break-words">{this.state.error.message}</p>
          )}
        </div>
      </div>
    );
  }
}
